import { z } from "zod";
import { WorkforceRoleSchema } from "./actor.js";
import { IdSchema } from "./primitives.js";
const profileText = (max) => z
    .string()
    .trim()
    .max(max)
    .regex(/^\P{Cc}*$/u);
export const WorkforcePreferencesSchema = z
    .object({
    landingPage: z
        .enum(["/", "/schedule", "/patients", "/attention", "/billing", "/admin"])
        .default("/"),
    rememberSidebar: z.boolean().default(true),
    sidebarCollapsed: z.boolean().default(false),
    reducedMotion: z.boolean().default(false),
    density: z.enum(["comfortable", "compact"]).default("comfortable"),
    appearance: z.enum(["light", "dark"]).default("light"),
})
    .strict();
// The browser decodes and resizes uploads before saving; SVG and arbitrary URLs
// are never accepted. The API additionally decodes and validates the PNG bytes.
export const WorkforcePhotoSchema = z
    .string()
    .max(100_000)
    .regex(/^data:image\/png;base64,iVBORw0KGgo[A-Za-z0-9+/]*={0,2}$/)
    .nullable();
export const WorkforceAccountFieldsSchema = z
    .object({
    displayName: profileText(160).min(2),
    // Physicians only; printed on forms that ask for the first and last name separately.
    legalFirstName: profileText(80),
    legalLastName: profileText(80),
    licenseNumber: profileText(80),
    licenseJurisdiction: profileText(120),
    signatureName: profileText(160),
    credentials: profileText(120),
    specialty: profileText(120),
    billingNumber: z.string().regex(/^(?:[0-9]{6})?$/),
    groupNumber: z.string().regex(/^[A-Z0-9]{4}$/),
    photoDataUrl: WorkforcePhotoSchema,
    preferences: WorkforcePreferencesSchema,
    // Physicians only, account owner only: a patient handout is drafted for review at every
    // visit they compose (EXECDEV-251). No default: zod applies defaults inside .partial(),
    // which would put the setting into every unrelated save.
    patientHandoutEveryVisit: z.boolean(),
})
    .strict();
export const WorkforceClinicSchema = z.object({
    tenantId: IdSchema,
    siteId: IdSchema,
    organizationName: z.string(),
    siteName: z.string(),
    role: WorkforceRoleSchema.exclude(["system_agent"]),
});
export const WorkforceAccountSchema = WorkforceAccountFieldsSchema.extend({
    // The account returns the legal names only once saved (migration 20261013125456).
    legalFirstName: profileText(80).default(""),
    legalLastName: profileText(80).default(""),
    // Returned only while on, on the physician's own account (EXECDEV-251). Optional and never
    // defaulted, so this API never adds the key for an application that predates it.
    patientHandoutEveryVisit: z.boolean().optional(),
    userId: IdSchema,
    tenantId: IdSchema,
    siteId: IdSchema,
    email: z.string(),
    role: WorkforceRoleSchema.exclude(["system_agent"]),
    organizationName: z.string(),
    siteName: z.string(),
    profileVersion: z.number().int().positive(),
    clinics: z.array(WorkforceClinicSchema),
});
export const SaveWorkforceAccountSchema = z
    .object({
    expectedVersion: z.number().int().positive(),
    idempotencyKey: z.string().min(8).max(128),
    changes: WorkforceAccountFieldsSchema.partial().refine((fields) => Object.keys(fields).length > 0),
})
    .strict();
//# sourceMappingURL=workforce-account.js.map